'use client'

import React, { useRef, useState, useEffect, useMemo } from 'react'
import { Howl, Howler } from 'howler'
import { formatTime, randomHash } from '@/utils'
import { Song, MusicPlayer, MusicAgent } from '@/types'
import SongControl from './SongControl'
import { TrackReport } from '@/api'
import { trackEvent } from '@/firebase'

declare global {
  interface Window {
    MusicPlayer: MusicPlayer
    MusicAgent: MusicAgent
    WebViewPlatform: string | undefined
  }
}

export default function MusicPlay({
  song,
  similars,
  config,
}: {
    song: Song
    similars: Song[]
    config: any
}) {
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0) // 播放进度，秒
  const [duration, setDuration] = useState(
    song?.songDuration ? Math.floor(Number(song.songDuration) / 1000) : 0
  ) // 歌曲总时长，秒
  const progressBarRef = useRef<HTMLAnchorElement>(null)
  const seekRef = useRef(0) // 播放进度frame 引用
  const playerRef = useRef<Howl | null>(null)
  const theme = config?.theme.split(' ')[0] || 'theme-a'
  const played = useRef(false) // 播放状态用于打点统计使用
  const vvid = useRef(randomHash(16))

  // 播放
  function handlePlay() {
    if (window.WebViewPlatform === 'player') {
      setPlaying(true)
    } else {
      playerRef.current?.play()
    }

    if (!played.current) {
      played.current = true

      TrackReport({ apiHost: `https://${window.location.host}`, params: { event: 'play_action', vvid: vvid.current, domain: `https://${window.location.host}`, id: song?.id, song: song?.songName } })
    }

    trackEvent('click-play', {
      play_id: song?.id,
    })

    console.log('handlePlay')
  }

  // 暂停
  function handlePause() {
    if (window.WebViewPlatform === 'player') {
      setPlaying(false)
    } else {
      playerRef.current?.pause()
    }

    trackEvent('click-pause', {
      play_id: song?.id,
    })

    console.log('handlePause')
  }

  // 上一首
  function handlePrev() {
    const $similars = document.querySelectorAll('#J_ulSimilars li a[href]') as NodeListOf<HTMLAnchorElement>
    const len = $similars.length

    if (len) {
      trackEvent('click-prev', {
        play_id: song?.id,
      })

      $similars[0]?.click()

      console.log('handlePrev')
    }
  }

  // 下一首
  function handleNext() {
    const $similars = document.querySelectorAll('#J_ulSimilars li a[href]') as NodeListOf<HTMLAnchorElement>
    const len = $similars.length

    if (len) {
      trackEvent('click-next', {
        play_id: song?.id,
      })

      $similars[len - 1]?.click()

      console.log('handleNext')
    }
  }

  // 定位播放: 秒
  function handleSeek(position: number) {
    if (window.WebViewPlatform === 'player') {
      setProgress(position)
    }

    if (position >= 0 && position < (playerRef.current?.duration() || 1)) {
      playerRef.current?.seek(position)
    }

    trackEvent('click-seek', {
      play_id: song?.id,
      play_position: position,
    })

    console.log('handleSeek')
  }

  // 初始化播放器
  async function initPlayer() {
    const howler = new Howl({
      src: [song?.songUrl],
      html5: window.WebViewPlatform === 'player',
      loop: window.WebViewPlatform !== 'player',
      preload: true,
      autoplay: window.WebViewPlatform !== 'player',
      volume: window.WebViewPlatform === 'player' ? 0 : 1.0,
      onplay: function () {
        console.log('onplay')

        seekRef.current = handlePlayerProgress()
        setPlaying(true)

        trackEvent('music-onplay', {
          play_id: song?.id,
        })
      },
      onload: function () {
        if (playerRef.current) {
          setDuration(playerRef.current?.duration())
        }
      },
      onend: function () {
        console.log('onend')

        setPlaying(false)
        cancelAnimationFrame(seekRef.current)

        played.current = false

        trackEvent('music-onend', {
          play_id: song?.id,
          play_duration: playerRef.current?.duration()
        })
      },
      onpause: function () {
        console.log('onpause', seekRef.current)

        setPlaying(false)
        cancelAnimationFrame(seekRef.current)

        trackEvent('music-onpause', {
          play_id: song?.id,
          play_seek: playerRef.current?.seek(),
          play_duration: playerRef.current?.duration()
        })
      },
      onstop: function () {
        console.log('onstop')

        setPlaying(false)
        cancelAnimationFrame(seekRef.current)

        trackEvent('music-onstop', {
          play_id: song?.id,
          play_seek: playerRef.current?.seek(),
          play_duration: playerRef.current?.duration()
        })
      },
      onseek: function () {
        console.log('onseek')

        trackEvent('music-onseek', {
          play_id: song?.id,
          play_seek: playerRef.current?.seek(),
          play_duration: playerRef.current?.duration()
        })
      },
    })

    return {
      howler,
      song,
    }
  }

  // 播放进度回调函数
  function handlePlayerProgress(): number {
    if (playerRef.current?.playing()) {
      setProgress(playerRef.current?.seek())
    }

    return requestAnimationFrame(handlePlayerProgress)
  }

  const progressPercent = useMemo(() => {
    return duration ? `${(progress / duration) * 100}%` : '0%'
  }, [progress, duration])

  // 点击播放条调整播放进度
  function handleProgressClick(event: React.MouseEvent) {
    const target = event.currentTarget as HTMLElement
    const rect = target.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const articleWidth = rect.width
    const percentage = clickX / articleWidth
    const _progress = percentage * duration

    setProgress(_progress)
    window.WebViewPlatform !== 'player' && playerRef.current?.seek(_progress)
  }

  // 拖动进度条调整播放进度
  // 拖动开始
  function handleProgressDragStart(event: React.MouseEvent): void {
    console.log(event)
  }

  // 拖动过程
  function handleProgressDragMove(event: React.MouseEvent): void {
    console.log(event)
  }

  // 拖动结束
  function handleProgressDragEnd(event: React.MouseEvent): void {
    console.log(event)
  }

  useEffect(() => {
    if (!window.MusicPlayer) {
      window.MusicPlayer = {}
    }

    // console.log('init window.MusicPlayer', similars)

    window.MusicPlayer.info = () => {
      return {
        similars: similars.map((s: Song) => {
          return { ...s, id: /^\d+\_\w+$/.test(String(s.id)) ? Number(String(s.id)?.split('_')[0]) : s.id }
        }), song
      }
    }
    window.MusicPlayer.play = handlePlay
    window.MusicPlayer.pause = handlePause
    window.MusicPlayer.previous = handlePrev
    window.MusicPlayer.next = handleNext
    window.MusicPlayer.seek = handleSeek
    window.MusicPlayer.duration = (): number => {
      return song?.songDuration ?? 0
    }
    window.MusicPlayer.vvid = (_vvid = ''): string => {
      vvid.current = _vvid

      return _vvid
    }

      ; (async () => {
        // PV 打点上报
        TrackReport({ apiHost: `https://${window.location.host}`, params: { event: 'play_view', vvid: vvid.current, domain: `https://${window.location.host}`, id: song?.id, song: song?.songName } })

        if (window.WebViewPlatform !== 'player') {
          const player = await initPlayer()
          playerRef.current = player.howler
        }
      })();

    return () => {
      cancelAnimationFrame(seekRef.current)

      Howler.stop()
      Howler.unload()
    }
  }, [])

  useEffect(() => {
    (async () => {
      await new Promise(resolve => setTimeout(resolve, 100))

      trackEvent('page-view-play', {
        play_id: song?.id,
      })
    })()
  }, [])

  return (
    <section className="section-play" data-song-id={song?.id}>
      {/* 封面图 */}
      <article className="block-cover">
        {
          ['theme-a', 'theme-e'].includes(theme) ? <img
            className="bg-cover"
            src={`/svg/bg-cover-${theme.split('-')[1] || ''}.svg`}
            alt="background cover of music playing"
          /> : null
        }

        <article className="wrap-cover">
          <img
            className={`img-cover ${theme === 'theme-a' ? 'animate-spin-slow' : ''}`}
            style={{ animationPlayState: playing ? 'running' : 'paused' }}
            src={song?.coverUrl}
            alt={`${song?.songName} - ${song?.singerName}`}
          />

          {['theme-b', 'theme-e'].includes(theme) && <SongControl {...{ theme, playing, handlePrev, handlePlay, handleNext, handlePause }} />}
        </article>
      </article>

      {/* 歌曲名 */}
      <article className="block-music">
        <h1 className="text-[32px] font-bold line-clamp-2">{song?.songName}</h1>
        <h6 className="text-[15px] font-normal opacity-50 mt-2 line-clamp-1">
          {song?.singerName}
        </h6>
      </article>

      {/* 播放进度 */}
      <article
        className="block-progress"
        ref={progressBarRef}
        onClick={handleProgressClick}
        id="J_barProgress"
      >
        <article className='wrap-progress'>
          <p
            className="bar-progress"
            style={{ width: progressPercent }}
          />
          <p
            className="circle-progress"
            draggable="true"
            style={{ left: progressPercent }}
          />
        </article>
      </article>

      {/* 播放时长 */}
      <article className="block-duration">
        <article className='wrap-duration w-full flex justify-between opacity-50'>
          <p className="mt-2 text-[13px] opacity-50">
            {progress ? formatTime(progress * 1000) : '00:00'}
          </p>
          <p className="mt-2 text-[13px] opacity-50">
            {duration ? formatTime(duration * 1000) : '--:--'}
          </p>
        </article>
      </article>

      {/* 播放控制 */}
      {['theme-a', 'theme-c', 'theme-d'].includes(theme) && <SongControl {...{ theme, playing, handlePrev, handlePlay, handleNext, handlePause }} />}
    </section>
  )
}
